/**
 * KORERIG showcase
 *
 * 1. <kr-turntable>  horizontaal sleepbare productviewer over een reeks stills.
 *    Geen autoplay: het product draait alleen als de bezoeker sleept, swipet of
 *    de pijltoetsen gebruikt. De viewer gaat pas open als alle frames binnen en
 *    gedecodeerd zijn, zodat er nooit een frame wordt overgeslagen. Het
 *    posterbeeld (identiek aan frame 1) staat er als los, direct geladen
 *    beeld.
 *
 *    Twee aparte maatregelen voor mobiel, die een verschillend probleem
 *    oplossen en dus geen van beide op zich genoeg zijn:
 *      - Het moment: de negentien losse sleepframes bouwen pas op een idle
 *        moment, zodat ze niet meedingen met de eerste paint. Ze worden
 *        hierdoor later geladen, niet minder — alle negentien komen alsnog
 *        binnen zodra de browser idle is.
 *      - De hoeveelheid: de breedte van elk frame wordt, alléén naar beneden,
 *        aangepast aan de daadwerkelijk weergegeven afmeting × devicePixelRatio
 *        (met een bodem van 480px). Op een smal telefoonscherm zijn dat dus
 *        kleinere bestanden dan de vaste 1000px die Liquid client-onafhankelijk
 *        klaarzet; op een brede of scherpe (retina) desktop verandert er niets.
 * 2. <kr-sequence>   rustige scrollgestuurde crossfade over de keyframes.
 *
 * Sinds de galerijherziening (2026-09) is elke kleur een eigen, losse slide in
 * <kr-gallery> (zie kr-product.js): alleen de Black-slide bevat deze
 * <kr-turntable>, de andere kleurslides zijn gewone stilstaande foto's. Zo kan
 * de 360°-bediening nooit een swipe op een andere kleur onderscheppen.
 *
 * Alles respecteert prefers-reduced-motion en laadt beelden gefaseerd.
 */

import { requestIdleCallback } from '@theme/utilities';

/** @returns {boolean} */
function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @param {HTMLElement} host
 * @returns {any}
 */
function readConfig(host) {
  const node = host.querySelector('script[type="application/json"]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || 'null');
  } catch (error) {
    console.error('[KORERIG] showcase config kon niet gelezen worden', error);
    return null;
  }
}

/**
 * Klaar betekent hier: binnen en gedecodeerd, dus meteen te tonen zonder dat de
 * eerste frame-tijd nog decodeerwerk kost.
 * @param {HTMLImageElement} img
 * @returns {Promise<boolean>}
 */
function whenReady(img) {
  return new Promise((resolve) => {
    const listen = () => {
      if (img.complete && img.naturalWidth > 0) {
        resolve(true);
        return;
      }
      img.addEventListener('load', () => resolve(true), { once: true });
      img.addEventListener('error', () => resolve(false), { once: true });
    };

    if (typeof img.decode === 'function') {
      img.decode().then(() => resolve(true), listen);
      return;
    }
    listen();
  });
}

/**
 * Herschrijft alléén de width-queryparameter van een CDN-URL, naar beneden.
 * Liquid zet elke frame-URL vast op een ruime desktopbreedte; op een smal of
 * niet-retina scherm is dat meer dan nodig. Ontbreekt de parameter, dan blijft
 * de URL ongewijzigd.
 * @param {string} url
 * @param {number} width
 * @returns {string}
 */
function withMaxWidth(url, width) {
  return url.replace(/([?&]width=)(\d+)/, (match, prefix, current) =>
    Number(current) > width ? `${prefix}${width}` : match
  );
}

/* ------------------------------------------------------------- turntable -- */

class KrTurntable extends HTMLElement {
  /** @type {string[]} */ #frames = [];
  /**
   * Weergavecorrectie per frame. De gegenereerde stills verschillen licht in
   * schaal en centrering; deze transform trekt alleen de weergave recht en
   * raakt het bronbestand niet. Frame 1 is de referentie en heeft dus geen
   * correctie; hij wordt precies een keer gezet, bij het aanmaken van het
   * element.
   * @type {string[]}
   */
  #transforms = [];
  /** @type {HTMLImageElement[]} */ #images = [];
  #index = 0;
  #ready = false;
  /** @type {AbortController | null} */ #controller = null;
  #interacted = false;
  /** @type {number | null} */ #pointerId = null;
  #startX = 0;
  #startIndex = 0;
  #accum = 0;

  connectedCallback() {
    const config = readConfig(this);
    this.#frames = config?.frames || [];
    this.#transforms = config?.transforms || [];

    this.stage = this.querySelector('[data-kr-stage]');
    this.poster = this.querySelector('[data-kr-poster]');
    this.hint = this.querySelector('[data-kr-hint]');
    this.live = this.querySelector('[data-kr-tt-live]');

    if (this.#frames.length < 2 || !this.stage) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.setAttribute('role', 'application');
    if (!this.getAttribute('aria-label')) {
      this.setAttribute('aria-label', config?.label || 'Product viewer. Drag left or right to rotate.');
    }

    // De handlers hangen er meteen aan maar doen niets tot alles binnen is.
    this.addEventListener('pointerdown', this.#onDown, { signal });
    this.addEventListener('pointermove', this.#onMove, { signal });
    this.addEventListener('pointerup', this.#onUp, { signal });
    this.addEventListener('pointercancel', this.#onUp, { signal });
    this.addEventListener('keydown', this.#onKey, { signal });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // Breedte per frame beperken tot wat er daadwerkelijk te zien is. Alleen
    // naar beneden: nooit groter opvragen dan de breedte die Liquid al
    // klaarzette. this is hier het element zelf (kr-turntable draagt de
    // .kr-tt-opmaak rechtstreeks), dus clientWidth is de echte weergavemaat.
    const displayWidth = this.getBoundingClientRect().width || this.clientWidth || 375;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.max(480, Math.round(displayWidth * dpr));
    this.#frames = this.#frames.map((url) => withMaxWidth(url, targetWidth));

    // Het posterbeeld (hierboven in de DOM, apart element) staat er al met
    // fetchpriority high. De losse sleepframes zijn alleen nodig zodra iemand
    // daadwerkelijk gaat slepen, dus die bouwen we pas op een idle moment —
    // dat verschuift wanneer ze laden, niet hoeveel: alle frames komen nog
    // steeds binnen, alleen pas als de browser er tijd voor heeft.
    requestIdleCallback(() => this.#preload());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Frames worden in hun echte numerieke volgorde opgehaald: eerst 1, dan 2,
   * dan 3. Zolang dat loopt blijft de poster staan en is slepen uitgeschakeld.
   * Ontbreekt er een frame, dan gaat de viewer bewust niet open: een draai met
   * een gat is slechter dan een stilstaand vooraanzicht.
   */
  async #preload() {
    for (let i = 0; i < this.#frames.length; i += 1) {
      const ok = await this.#build(i);
      if (!ok) {
        console.warn('[KORERIG] turntable frame kon niet laden:', i + 1);
        return;
      }
      if (!this.isConnected) return;
    }

    this.#ready = true;
    this.dataset.ready = 'true';
    this.setAttribute('tabindex', '0');
    this.#show(0);
    this.poster?.setAttribute('data-hidden', 'true');
  }

  /**
   * @param {number} i
   * @returns {Promise<boolean>}
   */
  #build(i) {
    if (this.#images[i]) return Promise.resolve(true);

    const img = new Image();
    img.decoding = 'async';
    img.alt = '';
    img.className = 'kr-tt__frame';

    const fix = this.#transforms[i];
    if (fix && fix !== 'none') img.style.transform = fix;

    this.#images[i] = img;
    this.stage.appendChild(img);
    img.src = this.#frames[i];

    return whenReady(img);
  }

  /**
   * Toont exact het gevraagde frame. Er is bewust geen terugval op het
   * dichtstbijzijnde geladen frame: dat sloeg stappen over tijdens het slepen.
   * @param {number} i
   */
  #show(i) {
    const n = this.#frames.length;
    const wanted = ((i % n) + n) % n;

    for (let j = 0; j < n; j += 1) {
      const img = this.#images[j];
      if (img) img.dataset.active = String(j === wanted);
    }
    this.#index = wanted;
  }

  #markInteracted() {
    if (this.#interacted) return;
    this.#interacted = true;
    this.hint?.setAttribute('data-hidden', 'true');
  }

  /** @param {PointerEvent} event */
  #onDown = (event) => {
    if (!this.#ready || this.#pointerId !== null) return;
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startIndex = this.#index;
    this.#accum = 0;
    this.setPointerCapture?.(event.pointerId);
    this.dataset.dragging = 'true';
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    if (!this.#ready || this.#pointerId !== event.pointerId) return;

    const width = this.clientWidth || 1;
    const dx = event.clientX - this.#startX;

    // Een hele omwenteling over ongeveer anderhalve elementbreedte.
    const steps = Math.round((dx / (width * 1.5)) * this.#frames.length);

    if (steps !== this.#accum) {
      this.#accum = steps;
      this.#markInteracted();
      this.#show(this.#startIndex - steps);
      event.preventDefault();
    }
  };

  /** @param {PointerEvent} event */
  #onUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.releasePointerCapture?.(event.pointerId);
    this.#pointerId = null;
    delete this.dataset.dragging;
    this.#announce();
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (!this.#ready) return;

    let delta = 0;
    if (event.key === 'ArrowLeft') delta = 1;
    if (event.key === 'ArrowRight') delta = -1;
    if (!delta) return;

    event.preventDefault();
    this.#markInteracted();
    this.#show(this.#index - delta);
    this.#announce();
  };

  #announce() {
    if (!this.live) return;
    this.live.textContent = `View ${this.#index + 1} of ${this.#frames.length}`;
  }
}

/* -------------------------------------------------------------- sequence -- */

/**
 * Scrollgestuurde crossfade over alle keyframes van de reeks.
 *
 * De reeks wordt in zijn geheel opgehaald en gedecodeerd zodra hij nadert. Pas
 * daarna loopt de interpolatie; tot dat moment blijft het eerste volledig
 * geladen beeld staan. Er staan altijd precies twee aangrenzende lagen aan,
 * nooit drie.
 *
 * data-blend bepaalt hoe die twee zich verhouden:
 *
 * - overlay   onderste laag 1, bovenste 0 -> 1. Voor beelden die het hele vlak
 *             vullen; er komt geen achtergrond doorheen.
 * - dissolve  onderste 1 - f, bovenste f, dus samen precies 1. Voor
 *             studiobeelden met verschillende composities: met overlay staan
 *             die te sterk over elkaar heen en ontstaat een spookbeeld.
 */
class KrSequence extends HTMLElement {
  /** @type {HTMLImageElement[]} */ #layers = [];
  /** @type {number | null} */ #raf = null;
  /** @type {IntersectionObserver | null} */ #near = null;
  /** @type {IntersectionObserver | null} */ #io = null;
  /** @type {AbortController | null} */ #controller = null;
  #visible = false;
  #ready = false;
  #loading = false;

  connectedCallback() {
    this.#layers = /** @type {HTMLImageElement[]} */ ([...this.querySelectorAll('[data-kr-layer]')]);
    if (this.#layers.length < 2) return;

    if (reducedMotion()) {
      this.#layers.forEach((layer, i) => {
        layer.style.opacity = i === this.#layers.length - 1 ? '1' : '0';
      });
      return;
    }

    this.#controller = new AbortController();

    // Naderende reeks: alles ophalen en decoderen voordat hij in beeld komt.
    this.#near = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.#prepare();
      },
      { rootMargin: '120% 0px 120% 0px' }
    );
    this.#near.observe(this);

    this.#io = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        if (this.#visible) this.#schedule();
      },
      { rootMargin: '25% 0px 25% 0px' }
    );
    this.#io.observe(this);

    window.addEventListener('scroll', this.#schedule, { passive: true, signal: this.#controller.signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal: this.#controller.signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#near?.disconnect();
    this.#io?.disconnect();
    this.#controller?.abort();
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #prepare() {
    if (this.#ready || this.#loading) return;
    this.#loading = true;

    let pending = this.#layers.length;
    let complete = true;

    for (const layer of this.#layers) {
      layer.loading = 'eager';
      whenReady(layer).then((ok) => {
        if (ok) layer.dataset.ready = 'true';
        else complete = false;

        pending -= 1;
        if (pending === 0) {
          this.#loading = false;
          this.#ready = complete;
        }
        this.#schedule(true);
      });
    }
  }

  /** Precies een update per schermverversing. */
  #schedule = (force = false) => {
    if ((!this.#visible && force !== true) || this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#update();
    });
  };

  #update() {
    if (!this.#ready) {
      /*
       * Nog niet alles binnen: het eerste volledig geladen beeld vasthouden.
       * Is er nog niets gedecodeerd, dan blijft laag 1 aan staan; anders zou het
       * lege vlak een tel lang grijs zijn.
       */
      const found = this.#layers.findIndex((layer) => layer.dataset.ready === 'true');
      const first = found < 0 ? 0 : found;
      this.#layers.forEach((layer, i) => {
        layer.style.opacity = i === first ? '1' : '0';
      });
      return;
    }

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || 1;

    // Ongewijzigde scrollafbeelding: de reeks loopt over dezelfde afstand.
    const raw = (vh * 0.85 - rect.top) / (vh * 0.6 + rect.height * 0.4);
    const p = Math.min(1, Math.max(0, raw));

    const last = this.#layers.length - 1;
    const pos = p * last;
    let base = Math.floor(pos);
    if (base > last - 1) base = last - 1;
    if (base < 0) base = 0;
    const frac = Math.min(1, Math.max(0, pos - base));

    const dissolve = this.dataset.blend === 'dissolve';

    this.#layers.forEach((layer, i) => {
      let value = 0;
      if (i === base) value = dissolve ? 1 - frac : 1;
      else if (i === base + 1) value = frac;
      layer.style.opacity = String(value);
    });
  }
}

/* ---------------------------------------------------------- registratie -- */

if (!customElements.get('kr-turntable')) customElements.define('kr-turntable', KrTurntable);
if (!customElements.get('kr-sequence')) customElements.define('kr-sequence', KrSequence);
